import React, { useState } from 'react';
import { Button, Box, Typography } from '@mui/material';
import { AccountCircle, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { signIn, confirmSignIn, getCurrentUser, signOut } from 'aws-amplify/auth';
import BWITextField from '../shared/BWITextField';
import { useSession } from '../../context/SessionContext';

// Define user session type
interface UserSession {
  signInStep: string;
}

// Define props for input forms
interface LoginFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  showPassword: boolean;
  setShowPassword: (showPassword: boolean) => void;
  handleLogin: () => void;
}

interface NewPasswordFormProps {
  newPassword: string;
  setNewPassword: (password: string) => void;
  showPassword: boolean;
  setShowPassword: (showPassword: boolean) => void;
  handleConfirmNewPassword: () => void;
}

const getErrorName = (error: unknown): string | undefined => {
  if (typeof error === 'object' && error !== null && 'name' in error) {
    return String(error.name);
  }

  return undefined;
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response
  ) {
    return Number(error.response.status);
  }

  return undefined;
};

const LoginContainer: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [requireNewPassword, setRequireNewPassword] = useState<boolean>(false);
  const [userSession, setUserSession] = useState<UserSession | null>(null);
  const navigate = useNavigate();
  const { loginUser } = useSession(); 

  const handleSuccessfulLogin = async () => {
    try {
      await getCurrentUser();
      await loginUser(); // Use the context function
      navigate('/');
    } catch (error) {
      console.error('Error updating user:', error);
      await signOut().catch((signOutError) => {
        console.error('Error clearing failed login session:', signOutError);
      });

      if (getErrorStatus(error) === 403) {
        alert('Cognito login succeeded, but this account is not provisioned in UMS yet. Add the user record/permissions in the backend and try again.');
        return;
      }

      alert('Login succeeded, but UMS failed to finish session setup. Please try again.');
    }
  };

  const handleLogin = async (): Promise<void> => {
    try {      
      const { nextStep } = await signIn({ username: email, password });

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setRequireNewPassword(true);
        setUserSession(nextStep);
      } else {
        await handleSuccessfulLogin();
      }
    } catch (error) {
      console.error('Login failed:', error);

      if (getErrorName(error) === 'UserAlreadyAuthenticatedException') {
        await signOut().catch((signOutError) => {
          console.error('Error clearing existing session:', signOutError);
        });
        alert('A previous login session was still active and has been cleared. Please log in again.');
        return;
      }

      alert('Invalid email or password. Please try again.');
    }
  };

  const handleConfirmNewPassword = async (): Promise<void> => {
    if (!userSession) return;

    try {
      await confirmSignIn({ challengeResponse: newPassword });
      await handleSuccessfulLogin();
    } catch (error) {
      console.error('Failed to confirm new password:', error);
      alert('Failed to set new password. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        maxWidth: 600,
        backgroundColor: 'background.paper',
        padding: 3,
        borderRadius: 2,
        boxShadow: 3,
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        marginTop: '3vh',
      }}
    >
      {requireNewPassword ? (
        <NewPasswordForm
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          showPassword={showNewPassword}
          setShowPassword={setShowNewPassword}
          handleConfirmNewPassword={handleConfirmNewPassword}
        />
      ) : (
        <LoginForm
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          handleLogin={handleLogin}
        />
      )}
    </Box>
  );
};

const LoginForm: React.FC<LoginFormProps> = ({
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  handleLogin
}) => (
  <>
    <BWITextField
      label="Email"
      value={email}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value.toLocaleLowerCase())}
      fullWidth
      margin="normal"
      startIcon={<AccountCircle />}
      data-cy="login-email"
    />
    <BWITextField
      label="Password"
      type={showPassword ? "text" : "password"}
      value={password}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
      fullWidth
      margin="normal"
      startIcon={<Lock />}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      data-cy="login-password"
    />
    <Button
      variant="contained"
      onClick={handleLogin}
      sx={{
        backgroundColor: '#d32f2f',
        color: '#fff',
        marginTop: 2,
        width: '100%',
        '&:hover': {
          backgroundColor: '#b71c1c',
        },
      }}
      data-cy="login-submit"
    >
      Log In
    </Button>
  </>
);

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({
  newPassword,
  setNewPassword,
  showPassword,
  setShowPassword,
  handleConfirmNewPassword
}) => (
  <>
    <Typography variant="h6" gutterBottom>
      Set a New Password
    </Typography>
    <BWITextField
      label="New Password"
      type={showPassword ? "text" : "password"}
      value={newPassword}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
      fullWidth
      margin="normal"
      startIcon={<Lock />}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      data-cy="new-password"
    />
    <Button
      variant="contained"
      onClick={handleConfirmNewPassword}
      sx={{
        backgroundColor: '#d32f2f',
        color: '#fff',
        marginTop: 2,
        width: '100%',
        '&:hover': {
          backgroundColor: '#b71c1c',
        },
      }}
      data-cy="new-password-submit"
    >
      Confirm New Password
    </Button>
  </>
);

export default LoginContainer;
